import React, { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SortTh, sortKeyOf, sortRows, useStoredSort, type SortState, type SortVal } from "@/lib/sort";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs, type ApiError } from "@/lib/api";
import { useCatalog } from "@/lib/catalog";
import { confirmar, confirmOpen, toast } from "./feedback";
import { PAGE_SIZES, usePageSize, usePageSizeStore } from "@/lib/pagesize";
import { Icon } from "./icons";

// ------------------------------------------------------------------ layout

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }): JSX.Element {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="muted small">{subtitle}</div>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, className = "" }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }): JSX.Element {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: {
  tabs: Array<{ key: T; label: string; count?: number | null; hidden?: boolean }>; value: T; onChange: (k: T) => void;
}): JSX.Element {
  return (
    <div className="tabs">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button key={t.key} className={`tab ${value === t.key ? "active" : ""}`} onClick={() => onChange(t.key)}>
          {t.label}{t.count ? <span className="tab-count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Menu em dois níveis (grupos + subabas), no padrão do Financeiro. O grupo segue a aba escolhida. */
export function GroupedTabs<T extends string>({ groups, value, onChange }: {
  groups: Array<{ key: string; label: string; tabs: Array<{ key: T; label: string; hidden?: boolean }> }>; value: T; onChange: (k: T) => void;
}): JSX.Element {
  const visible = groups.map((g) => ({ ...g, tabs: g.tabs.filter((t) => !t.hidden) })).filter((g) => g.tabs.length);
  const current = visible.find((g) => g.tabs.some((t) => t.key === value)) ?? visible[0];
  return (
    <div className="fin-nav">
      <div className="fin-groups">
        {visible.map((g) => <button key={g.key} className={`fin-group ${g.key === current?.key ? "active" : ""}`} onClick={() => onChange(g.tabs[0]!.key)}>{g.label}</button>)}
      </div>
      {current && current.tabs.length > 1 && (
        <div className="fin-subs">
          {current.tabs.map((t) => <button key={t.key} className={`fin-sub ${t.key === value ? "active" : ""}`} onClick={() => onChange(t.key)}>{t.label}</button>)}
        </div>
      )}
    </div>
  );
}

/** Janelas abertas (a de cima responde ao Esc) e quantas têm algo preenchido. */
const modalStack: number[] = [];
let modalSeq = 0;
const dirtyModals = new Set<number>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => { if (dirtyModals.size) { e.preventDefault(); e.returnValue = ""; } });
}
const CLOSE_WORDS = /^(cancelar|fechar|voltar|sair)$/i;

/**
 * Janela modal. Se algo foi digitado/alterado, fechar (✕, Esc, Cancelar, clique fora)
 * pergunta antes de descartar; clique fora não fecha janela preenchida. Enter num campo
 * vai para o próximo (nunca fecha); Ctrl/⌘+Enter aciona o botão principal.
 */
export function Modal({ title, onClose, children, footer, wide }: {
  title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean | "xl";
}): JSX.Element {
  const [id] = useState(() => ++modalSeq);
  const dirty = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const guardedClose = async () => {
    if (dirty.current && !(await confirmar("Você começou a preencher esta janela. Descartar o que foi digitado?", { confirm: "Descartar", danger: true }))) return;
    dirtyModals.delete(id);
    closeRef.current();
  };
  const guardRef = useRef(guardedClose);
  guardRef.current = guardedClose;
  useEffect(() => {
    modalStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || confirmOpen() || modalStack[modalStack.length - 1] !== id) return;
      e.stopPropagation();
      void guardRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = modalStack.indexOf(id); if (i >= 0) modalStack.splice(i, 1);
      dirtyModals.delete(id);
    };
  }, [id]);
  const markDirty = (e: React.SyntheticEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("[data-no-dirty]")) return;
    dirty.current = true; dirtyModals.add(id);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    const t = e.target as HTMLElement;
    if ((e.ctrlKey || e.metaKey)) {
      const primary = box.current?.querySelector<HTMLButtonElement>(".modal-foot .btn.primary:not(:disabled)");
      if (primary) { e.preventDefault(); primary.click(); }
      return;
    }
    if (t.tagName === "TEXTAREA" || t.closest("[data-enter-ok]")) return;
    if (t.tagName === "INPUT" || t.tagName === "SELECT") {
      e.preventDefault();
      const fields = [...(box.current?.querySelectorAll<HTMLElement>(".modal-body input:not([type=hidden]):not(:disabled), .modal-body select:not(:disabled), .modal-body textarea:not(:disabled)") ?? [])];
      const next = fields[fields.indexOf(t) + 1];
      next?.focus();
    }
  };
  // "Cancelar"/"Fechar" do rodapé também passam pela confirmação.
  const onFootClick = (e: React.MouseEvent) => {
    const btn = (e.target as HTMLElement).closest("button");
    if (!btn || btn.classList.contains("primary") || btn.classList.contains("danger") || !CLOSE_WORDS.test(btn.textContent?.trim() ?? "")) return;
    if (!dirty.current) return;
    e.preventDefault(); e.stopPropagation();
    void guardedClose();
  };
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !dirty.current) onClose(); }}>
      <div ref={box} className={`modal ${wide ? "wide" : ""} ${wide === "xl" ? "xl" : ""}`} role="dialog" aria-modal="true" onKeyDown={onKeyDown}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="btn ghost" onClick={() => void guardedClose()} aria-label="Fechar" tabIndex={-1}>✕</button>
        </div>
        <div className="modal-body" onInputCapture={markDirty} onChangeCapture={markDirty}>{children}</div>
        {footer && <div className="modal-foot" onClickCapture={onFootClick}>{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, hint, span, required }: { label: string; children: ReactNode; hint?: ReactNode; span?: 1 | 2 | 3; required?: boolean }): JSX.Element {
  return (
    <label className={`fld span-${span ?? 1}`}>
      <span className="fld-label">{label}{required && <span className="req"> *</span>}</span>
      {children}
      {hint && <span className="fld-hint">{hint}</span>}
    </label>
  );
}

export function FormGrid({ children, cols = 2 }: { children: ReactNode; cols?: 1 | 2 | 3 }): JSX.Element {
  return <div className={`form-grid cols-${cols}`}>{children}</div>;
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "ok" | "warn" | "bad" | "info" | "muted" }): JSX.Element {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function Stat({ label, value, tone, onClick, hint }: { label: string; value: ReactNode; tone?: "ok" | "warn" | "bad" | "info"; onClick?: () => void; hint?: ReactNode }): JSX.Element {
  return (
    <div className={`stat ${tone ?? ""} ${onClick ? "clickable" : ""}`} onClick={onClick} role={onClick ? "button" : undefined}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }): JSX.Element | null {
  if (!error) return null;
  const e = error as ApiError;
  return <div className="error">{e.message || "Erro inesperado"}{e.status === 403 ? " (sem permissão)" : ""}</div>;
}

/** Carregamento no formato do conteúdo (linhas de esqueleto). */
export function Loading({ lines = 3 }: { what?: string; lines?: number }): JSX.Element {
  return <div className="skeleton" aria-busy="true">{Array.from({ length: lines }, (_, i) => <div key={i} style={{ width: `${90 - i * 18}%` }} />)}</div>;
}

export function Empty({ children, icon = "inbox" }: { children: ReactNode; icon?: string }): JSX.Element {
  return <div className="empty"><Icon name={icon} />{children}</div>;
}

// ------------------------------------------------------------------- tabela

export interface Column<T> {
  key: string;
  label: ReactNode;
  render: (row: T) => ReactNode;
  width?: string | number;
  align?: "left" | "right" | "center";
  /** Ordenar: valor da coluna (sem isso, usa o texto exibido); false = não ordena. */
  sort?: ((row: T) => SortVal) | false;
  /** Lista paginada pelo servidor: nome da ordenação na API (só colunas com sortKey ficam clicáveis). */
  sortKey?: string;
}

export function DataTable<T>({ columns, rows, onRowClick, empty = "Nada por aqui.", rowKey, rowClass, paginate = true, sortId, server }: {
  columns: Array<Column<T>>; rows: T[] | undefined; onRowClick?: (r: T) => void; empty?: ReactNode;
  rowKey: (r: T) => string; rowClass?: (r: T) => string;
  /** Listas longas carregadas de uma vez são paginadas aqui (10/25/50/100). */
  paginate?: boolean;
  /** Identifica a lista para lembrar a ordenação escolhida (padrão: página + colunas). */
  sortId?: string;
  /** Ordenação feita pelo servidor (listas paginadas na API). */
  server?: { sort: SortState; set: (s: SortState) => void };
}): JSX.Element {
  const size = usePageSize();
  const [skip, setSkip] = useState(0);
  const id = sortId ?? `${typeof window !== "undefined" ? window.location.pathname.replace(/[0-9a-f-]{36}/g, ":id") : ""}|${columns.map((c) => c.key).join(",")}`;
  const [local, setLocal] = useStoredSort(id);
  const sort = server ? server.sort : local;
  const setSort = server ? server.set : setLocal;
  const total = rows?.length ?? 0;
  useEffect(() => { if (skip >= total && skip > 0) setSkip(0); }, [total, skip]);
  const valueOf = (c: Column<T>, r: T) => sortKeyOf(c.sort ? c.sort(r) : (c.render(r) as SortVal));
  // Coluna ordenável (na tela): tem valor explícito ou ao menos 2 valores diferentes no texto exibido
  // (colunas de botão/ação, iguais em todas as linhas, ficam de fora).
  const clientSortable = useMemo(() => {
    if (server || !rows) return new Set<string>();
    const ok = new Set<string>();
    for (const c of columns) {
      if (c.sort === false) continue;
      if (c.sort) { ok.add(c.key); continue; }
      const seen = new Set<string | number>();
      for (const r of rows.slice(0, 400)) { const v = valueOf(c, r); if (v !== null) seen.add(v); if (seen.size > 1) break; }
      if (seen.size > 1) ok.add(c.key);
    }
    return ok;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, server, columns.length]);
  const sortable = (c: Column<T>) => (server ? !!c.sortKey : clientSortable.has(c.key));
  const active = server ? null : columns.find((c) => c.key === sort.key && sortable(c));
  const ordered = useMemo(() => (rows && active && sort.dir ? sortRows(rows, (r) => valueOf(active, r), sort.dir) : rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, active?.key, sort.dir]);
  if (!ordered) return <Loading />;
  if (!ordered.length) return <Empty>{empty}</Empty>;
  const paged = paginate && ordered.length > size;
  const view = paged ? ordered.slice(skip, skip + size) : ordered;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>{columns.map((c) => {
            const k = server ? c.sortKey ?? null : sortable(c) ? c.key : null;
            const on = !!k && sort.key === k;
            return <SortTh key={c.key} label={c.label} k={k} sort={on ? sort : { key: sort.key, dir: sort.dir }} onSort={(next) => { setSort(next); setSkip(0); }} style={{ width: c.width, textAlign: c.align }} />;
          })}</tr>
        </thead>
        <tbody>
          {view.map((r) => (
            <tr key={rowKey(r)} className={`${onRowClick ? "clickable" : ""} ${rowClass?.(r) ?? ""}`} onClick={() => onRowClick?.(r)}>
              {columns.map((c) => <td key={c.key} style={{ textAlign: c.align }}>{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {paged && <Pager total={total} take={size} skip={skip} onChange={setSkip} />}
    </div>
  );
}

/** Paginação com escolha de itens por página (lembrada por pessoa). Ao mudar o tamanho, volta à 1ª página. */
export function Pager({ total, take, skip, onChange }: { total: number; take: number; skip: number; onChange: (skip: number) => void }): JSX.Element | null {
  const { size, set } = usePageSizeStore();
  const page = Math.floor(skip / take) + 1;
  const pages = Math.max(1, Math.ceil(total / take));
  const sizeSel = (
    <label className="row nowrap small muted" data-no-dirty>
      <select className="input" style={{ width: 70, padding: "2px 6px" }} value={size} onChange={(e) => { set(Number(e.target.value)); onChange(0); }}>
        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select> por página
    </label>
  );
  if (total <= take && take <= 10) return <div className="pager"><span className="muted small">{total} registro(s)</span></div>;
  if (total <= take) return <div className="pager"><span className="muted small">{total} registro(s)</span>{total > 10 && sizeSel}</div>;
  return (
    <div className="pager">
      <span className="muted small">{total} registros · página {page} de {pages}</span>
      {sizeSel}
      <button className="btn" disabled={page <= 1} onClick={() => onChange(0)} title="Primeira">«</button>
      <button className="btn" disabled={page <= 1} onClick={() => onChange(Math.max(0, skip - take))}>Anterior</button>
      <button className="btn" disabled={page >= pages} onClick={() => onChange(skip + take)}>Próxima</button>
      <button className="btn" disabled={page >= pages} onClick={() => onChange((pages - 1) * take)} title="Última">»</button>
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder = "Buscar…" }: { value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  useEffect(() => {
    const t = setTimeout(() => { if (v !== value) onChange(v); }, 350);
    return () => clearTimeout(t);
  }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
  return <input className="input search" data-no-dirty value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} />;
}

// --------------------------------------------------------- ações/mutações

/**
 * Mutação com invalidação de consultas e erro exibível.
 *   const act = useAct((b) => api.post(...), ["processos"]);
 */
export function useAct<TIn = void, TOut = unknown>(
  fn: (input: TIn) => Promise<TOut>, invalidate: string[][] = [], onSuccess?: (out: TOut) => void,
  /** Aviso no canto ao concluir (false = silencioso). */
  message: string | false = "Alterações salvas",
) {
  const qc = useQueryClient();
  return useMutation<TOut, ApiError, TIn>({
    mutationFn: fn,
    onSuccess: async (out) => {
      await Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
      if (message) toast.ok(message);
      onSuccess?.(out);
    },
  });
}

/** Diálogo que exige justificativa (cancelar, reabrir, prazo fatal…). */
export function ReasonDialog({ title, label = "Justificativa", confirm = "Confirmar", onClose, onSubmit, min = 3, children, suggestions }: {
  title: string; label?: string; confirm?: string; onClose: () => void; onSubmit: (reason: string) => Promise<unknown>; min?: number; children?: ReactNode;
  /** Motivos prontos (um clique preenche; dá para completar o texto). */
  suggestions?: string[];
}): JSX.Element {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button className="btn" onClick={onClose}>Voltar</button>
        <button className="btn primary" disabled={busy || reason.trim().length < min} onClick={async () => {
          setBusy(true); setErr(null);
          try { await onSubmit(reason.trim()); onClose(); } catch (e) { setErr(e); } finally { setBusy(false); }
        }}>{confirm}</button>
      </>
    }>
      <div className="stack">
        {children}
        {suggestions?.length ? <div className="row wrap gap-8">{suggestions.map((s) => <button key={s} type="button" className={`chip ${reason === s ? "on" : ""}`} onClick={() => setReason(s)}>{s}</button>)}</div> : null}
        <Field label={label} required><textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
        <ErrorBox error={err} />
      </div>
    </Modal>
  );
}

// ------------------------------------------------------ seletores comuns

// ------------------------------------------------------ lista pesquisável

export interface ComboOption { value: string; label: string; hint?: string }
const foldTxt = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/**
 * Lista com busca (substitui <select> em listas que podem crescer):
 * digite para filtrar, setas + Enter para escolher, Esc para fechar.
 */
export function Combobox({ value, onChange, options, emptyLabel = "—", allowEmpty = true, disabled, placeholder = "Digite para buscar…" }: {
  value: string | null | undefined; onChange: (v: string | null) => void; options: ComboOption[];
  emptyLabel?: string; allowEmpty?: boolean; disabled?: boolean; placeholder?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const all: ComboOption[] = [...(allowEmpty ? [{ value: "", label: emptyLabel }] : []), ...options];
  const fq = foldTxt(q.trim());
  const shown = fq ? all.filter((o) => o.value && foldTxt(`${o.label} ${o.hint ?? ""}`).includes(fq)) : all;
  const current = options.find((o) => o.value === value);
  useEffect(() => { setHi(0); }, [q, open]);
  useEffect(() => { list.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`)?.scrollIntoView({ block: "nearest" }); }, [hi]);
  const choose = (o: ComboOption | undefined) => { if (!o) return; onChange(o.value || null); setOpen(false); setQ(""); };
  const openIt = () => { if (disabled) return; setOpen(true); setTimeout(() => input.current?.focus(), 0); };
  return (
    <div className={`combo ${disabled ? "disabled" : ""}`} ref={ref}>
      <button type="button" className="input combo-btn" disabled={disabled} onClick={() => (open ? setOpen(false) : openIt())}
        onKeyDown={(e) => { if (e.key === "ArrowDown" || e.key === "Enter" || (e.key.length === 1 && !e.metaKey && !e.ctrlKey)) { e.preventDefault(); if (e.key.length === 1) setQ(e.key); openIt(); } }}>
        <span className={current ? "" : "muted"}>{current ? current.label : emptyLabel}</span>
        <span className="combo-caret">▾</span>
      </button>
      {open && (
        <div className="combo-pop">
          <input ref={input} className="input combo-search" value={q} placeholder={placeholder} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, shown.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); choose(shown[hi]); }
              else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
            }} />
          <div className="combo-list" ref={list}>
            {shown.map((o, i) => (
              <button type="button" key={o.value || "__empty"} data-i={i} className={`picker-item ${i === hi ? "hi" : ""} ${o.value === (value ?? "") ? "sel" : ""}`}
                onMouseEnter={() => setHi(i)} onClick={() => choose(o)}>
                {o.value ? o.label : <span className="muted">{o.label}</span>}
                {o.hint && <span className="muted small"> · {o.hint}</span>}
              </button>
            ))}
            {!shown.length && <div className="muted small pad">Nada encontrado</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export function UserSelect({ value, onChange, allowEmpty = true, emptyLabel = "—" }: { value: string | null | undefined; onChange: (v: string | null) => void; allowEmpty?: boolean; emptyLabel?: string }): JSX.Element {
  const { data } = useCatalog();
  const opts = (data?.users ?? []).map((u) => ({ value: u.id, label: u.displayName }));
  return <Combobox value={value} onChange={onChange} options={opts} allowEmpty={allowEmpty} emptyLabel={allowEmpty ? emptyLabel : "Selecione…"} />;
}

export function AreaSelect({ value, onChange, unitId, allowEmpty }: { value: string | null | undefined; onChange: (v: string | null) => void; unitId?: string; allowEmpty?: boolean }): JSX.Element {
  const { data } = useCatalog();
  const unit = data?.units.find((u) => u.id === unitId);
  const areas = (data?.areas ?? []).filter((a) => a.active && (!unit || unit.areaIds.includes(a.id)));
  return <Combobox value={value} onChange={onChange} options={areas.map((a) => ({ value: a.id, label: a.name }))} emptyLabel={allowEmpty ? "Todas" : "Selecione…"} />;
}

export function UnitSelect({ value, onChange, allowEmpty }: { value: string | null | undefined; onChange: (v: string | null) => void; allowEmpty?: boolean }): JSX.Element {
  const { data } = useCatalog();
  const opts = (data?.units ?? []).filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }));
  return <Combobox value={value} onChange={onChange} options={opts} allowEmpty={!!allowEmpty} emptyLabel={allowEmpty ? "—" : "Selecione…"} />;
}

export function TypeSelect({ value, onChange }: { value: string | null | undefined; onChange: (v: string | null) => void }): JSX.Element {
  const { data } = useCatalog();
  const opts = (data?.activityTypes ?? []).filter((t) => t.active).map((t) => ({ value: t.id, label: t.name }));
  return <Combobox value={value} onChange={onChange} options={opts} emptyLabel="Sem tipo" />;
}

/** Busca e escolhe pessoa, caso ou processo. */
export type EntityKind = "pessoa" | "caso" | "processo";
export interface EntityRef { kind: EntityKind; id: string; label: string }

const KIND_LABEL: Record<EntityKind, string> = { pessoa: "Pessoa", caso: "Caso", processo: "Processo" };
interface Hit { kind: EntityKind | "atividade"; id: string; label: string; context: string }

/**
 * Busca com contexto (nome, CPF/CNPJ, nº do caso, CNJ inteiro ou parte): cada sugestão traz o contexto
 * — ex.: "Bruno Silva · CPF … · 3 casos", "Caso #45 … · Trabalhista", "0001234-… · Bruno × Empresa · 2ª Vara".
 */
export function EntityPicker({ kinds, value, onChange, placeholder }: {
  kinds: EntityKind[]; value: EntityRef | null; onChange: (v: EntityRef | null) => void; placeholder?: string;
}): JSX.Element {
  const [q, setQ] = useState("");
  const [deb, setDeb] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { const t = setTimeout(() => setDeb(q.trim()), 200); return () => clearTimeout(t); }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ["busca", kinds.join(","), deb],
    queryFn: () => api.get<Hit[]>(`/busca${qs({ q: deb, tipos: kinds.join(","), limite: 8 })}`),
    enabled: open && deb.length >= 2,
  });
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const hits = (data ?? []).filter((h): h is Hit & { kind: EntityKind } => (kinds as string[]).includes(h.kind));
  const pick = (h: Hit & { kind: EntityKind }) => { onChange({ kind: h.kind, id: h.id, label: h.label }); setOpen(false); setQ(""); };

  if (value) {
    return (
      <div className="picked">
        <Badge tone="info">{KIND_LABEL[value.kind]}</Badge> <span>{value.label}</span>
        <button type="button" className="link-btn" onClick={() => onChange(null)}>trocar</button>
      </div>
    );
  }
  return (
    <div className="picker" ref={ref}>
      <input className="input" value={q} placeholder={placeholder ?? `Buscar ${kinds.map((k) => KIND_LABEL[k].toLowerCase()).join(", ")} por nome, CPF, nº ou CNJ…`} onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setSel(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setSel((x) => Math.min(x + 1, hits.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setSel((x) => Math.max(x - 1, 0)); }
          if (e.key === "Enter" && hits[sel]) { e.preventDefault(); pick(hits[sel]!); }
          if (e.key === "Escape") setOpen(false);
        }} />
      {open && q.trim().length >= 2 && (
        <div className="picker-list">
          {hits.map((h, i) => (
            <button type="button" key={`${h.kind}${h.id}`} className={`picker-item ${i === sel ? "sel" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => pick(h)}>
              <span className="pk-kind">{KIND_LABEL[h.kind]}</span>
              <span className="pk-main">{h.label}{h.context && <small>{h.context}</small>}</span>
            </button>
          ))}
          {data && !hits.length && !isFetching && <div className="muted small pad">Nada encontrado para “{q}”.</div>}
          {isFetching && !data && <div className="muted small pad">Buscando…</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- etapas

export function StageBadge({ stageId }: { stageId: string | null | undefined }): JSX.Element {
  const { data } = useCatalog();
  const st = data?.stages.find((x) => x.id === stageId);
  if (!st) return <span className="badge muted">sem etapa</span>;
  return <span className="badge" style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}55` }}>{st.name}</span>;
}

export function StageSelect({ scope, value, onChange, emptyLabel = "Sem etapa" }: { scope: "processo" | "caso"; value: string | null | undefined; onChange: (v: string | null) => void; emptyLabel?: string }): JSX.Element {
  const { data } = useCatalog();
  const opts = (data?.stages ?? []).filter((x) => x.scope === scope && (x.active || x.id === value)).map((x) => ({ value: x.id, label: x.name }));
  return <Combobox value={value} onChange={onChange} options={opts} emptyLabel={emptyLabel} />;
}

/** Tipos de ação (Família → Alimentos, Divórcio…) de uma área. */
export function ActionTypeSelect({ areaId, value, onChange, emptyLabel = "Selecione…" }: { areaId: string | null | undefined; value: string | null | undefined; onChange: (v: string | null) => void; emptyLabel?: string }): JSX.Element {
  const { data } = useCatalog();
  // Sem área definida (ex.: processo antes de escolher o caso): todos os tipos, com a área no nome.
  const options = areaId
    ? (data?.areas.find((a) => a.id === areaId)?.subjects.filter((s) => s.active || s.id === value) ?? []).map((s) => ({ value: s.id, label: s.name }))
    : (data?.areas ?? []).flatMap((a) => a.subjects.filter((s) => s.active || s.id === value).map((s) => ({ value: s.id, label: `${a.name} · ${s.name}` })));
  return <Combobox value={value} onChange={onChange} options={options} emptyLabel={emptyLabel} />;
}

/** Copia um texto (ex.: número do processo) e avisa. */
export function CopyButton({ text, title = "Copiar" }: { text: string; title?: string }): JSX.Element {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="copy-btn" title={title} aria-label={title}
      onClick={(e) => {
        e.preventDefault(); e.stopPropagation();
        void navigator.clipboard.writeText(text).then(() => { setOk(true); toast.ok("Copiado"); setTimeout(() => setOk(false), 1500); });
      }}>{ok ? "✓" : "⧉"}</button>
  );
}

/** Número do processo (CNJ ou identificador) com botão de copiar. */
export function Cnj({ value, className = "mono", fallback = "—" }: { value: string | null | undefined; className?: string; fallback?: ReactNode }): JSX.Element {
  if (!value) return <>{fallback}</>;
  return <span className="cnj-copy"><span className={className}>{value}</span><CopyButton text={value} title="Copiar número do processo" /></span>;
}

/** Prazo fatal ligado (da atividade principal, do fluxo, da dependente ou da mesma intimação). */
export interface RelatedFatal { date: string; source: "principal" | "fluxo" | "dependente" | "intimacao" | string; label: string; activityId: string | null }
const RF_LABEL: Record<string, string> = { principal: "da atividade principal", fluxo: "do fluxo", dependente: "da atividade que depende desta", intimacao: "da mesma intimação" };
export function relatedFatalTitle(r: RelatedFatal): string { return `Prazo fatal ${RF_LABEL[r.source] ?? "ligado"}: ${r.label}`; }

/** Prazo fatal da própria atividade ou, sem ele, o prazo fatal a que ela está ligada (marcado com ↳). */
export function FatalDate({ own, related, overdue, short, open = true }: { own?: string | null; related?: RelatedFatal | null; overdue?: boolean; short?: boolean; open?: boolean }): JSX.Element {
  const fmt = (iso: string) => { const d = iso.slice(0, 10); return short ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`; };
  if (own) return <span className={overdue ? "text-bad" : "text-warn"} title="Prazo fatal">{short ? "⚑ " : ""}{fmt(own)}</span>;
  if (related) {
    const late = open && related.date < new Date().toISOString().slice(0, 10);
    return <span className={late ? "text-bad" : "text-warn"} style={{ fontStyle: "italic", opacity: 0.85 }} title={relatedFatalTitle(related)}>↳ {fmt(related.date)}</span>;
  }
  return <span className="muted">—</span>;
}
